import React, { useState, useEffect, useRef } from 'react';
import { Paper, Title, TextInput, Select, Textarea, Grid, Button, Group, Text, Divider, Alert } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import { PrimaryButton } from '../../components/UI';
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField';
import { useAuth } from '../../contexts/AuthContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import { isMinor, getPreviousDancerInfo } from '../../services/registrationService';
import { getActiveSeason } from '../../config/seasons';

// Copied by "Reuse my <year> info": details that rarely change. Studio, level,
// training, experience, teacher and medical info are left for a fresh answer.
const REUSABLE_FIELDS = [
  'first_name', 'last_name', 'name_pronunciation', 'date_of_birth', 'gender',
  'phone_number', 'address_street', 'address_city', 'address_province',
  'address_postal_code', 'address_country', 'parent_guardian_name', 'parent_guardian_email',
  'emergency_contact_name', 'emergency_contact_relationship', 'emergency_contact_phone',
  'emergency_contact_email',
];

/**
 * Step 1: Initial Information Form
 * Collects personal information, dance background, and emergency contact details
 */
const Step1Information = ({
  dancerInfo,
  saveDancerInfo,
  saving,
  goToNextStep,
  goToPreviousStep,
  goToStep,
  isComplete = false,
  readOnly = false
}) => {
  const { user } = useAuth();
  // One email per account: the dancer email is the verified sign-in email.
  // Paid registrations keep what's on file (it may have been corrected by hand).
  const accountEmail = user?.email || '';

  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    name_pronunciation: '',
    date_of_birth: null,
    gender: '',
    email: accountEmail,
    phone_number: '',
    address_street: '',
    address_city: '',
    address_province: '',
    address_postal_code: '',
    address_country: 'Canada',

    // Parent/Guardian (if under 18)
    parent_guardian_name: '',
    parent_guardian_email: '',

    // Dance Background
    years_training: '',
    current_studio: '',
    teacher_name: '',
    competition_experience: '',
    current_level: '',

    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    emergency_contact_email: '',

    // Medical Information
    medical_conditions: '',
    special_accommodations: ''
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isUnder18, setIsUnder18] = useState(false);
  // { year, dancerInfo } from an earlier season, offered once on a fresh Step 1.
  const [reuseOffer, setReuseOffer] = useState(null);

  const startedFresh = !dancerInfo?.first_name;
  useEffect(() => {
    if (readOnly || isComplete || !startedFresh || !user?.id) return;
    let cancelled = false;
    getPreviousDancerInfo(user.id, getActiveSeason().year)
      .then(found => { if (!cancelled) setReuseOffer(found); })
      .catch(err => console.warn('Could not look up a previous registration:', err));
    return () => { cancelled = true; };
  }, [readOnly, isComplete, startedFresh, user?.id]);

  const applyReuse = () => {
    const previous = reuseOffer.dancerInfo;
    setFormData(prev => {
      const next = { ...prev };
      REUSABLE_FIELDS.forEach(field => {
        if (previous[field]) next[field] = previous[field];
      });
      return next;
    });
    setReuseOffer(null);
  };

  // Style for left-aligned labels
  const inputStyles = {
    label: { textAlign: 'left' },
    description: { textAlign: 'left' }
  };

  /**
   * Load saved dancer info into the form once per registration. Every save
   * (autosave included) hands back a new dancerInfo; re-loading from it would
   * overwrite whatever was typed while the save was in flight.
   */
  const hydratedForRef = useRef(null);
  useEffect(() => {
    if (dancerInfo && hydratedForRef.current !== dancerInfo.registration_id) {
      hydratedForRef.current = dancerInfo.registration_id;
      setFormData({
        first_name: dancerInfo.first_name || '',
        last_name: dancerInfo.last_name || '',
        name_pronunciation: dancerInfo.name_pronunciation || '',
        date_of_birth: dancerInfo.date_of_birth || '',
        gender: dancerInfo.gender || '',
        email: isComplete ? (dancerInfo.email || accountEmail) : (accountEmail || dancerInfo.email || ''),
        phone_number: dancerInfo.phone_number || '',
        address_street: dancerInfo.address_street || '',
        address_city: dancerInfo.address_city || '',
        address_province: dancerInfo.address_province || '',
        address_postal_code: dancerInfo.address_postal_code || '',
        address_country: dancerInfo.address_country || 'Canada',
        parent_guardian_name: dancerInfo.parent_guardian_name || '',
        parent_guardian_email: dancerInfo.parent_guardian_email || '',
        years_training: dancerInfo.years_training || '',
        current_studio: dancerInfo.current_studio || '',
        teacher_name: dancerInfo.teacher_name || '',
        competition_experience: dancerInfo.competition_experience || '',
        current_level: dancerInfo.current_level || '',
        emergency_contact_name: dancerInfo.emergency_contact_name || '',
        emergency_contact_relationship: dancerInfo.emergency_contact_relationship || '',
        emergency_contact_phone: dancerInfo.emergency_contact_phone || '',
        emergency_contact_email: dancerInfo.emergency_contact_email || '',
        medical_conditions: dancerInfo.medical_conditions || '',
        special_accommodations: dancerInfo.special_accommodations || ''
      });

      if (dancerInfo.date_of_birth) {
        setIsUnder18(isMinor(dancerInfo.date_of_birth));
      }
    }
  }, [dancerInfo, accountEmail, isComplete]);

  /**
   * Check if under 18 when DOB changes
   */
  useEffect(() => {
    if (formData.date_of_birth) {
      setIsUnder18(isMinor(formData.date_of_birth));
    }
  }, [formData.date_of_birth]);

  /**
   * Handle input change
   */
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Auto-save functionality
   */
  const saveFormData = async (data) => {
    // Don't auto-save if required fields are empty
    if (!data.date_of_birth || !data.first_name || !data.last_name || !data.email) {
      console.log('⏭️ Skipping auto-save: Required fields not filled');
      return;
    }

    // Format data for database
    const dbData = {
      ...data,
      email: isComplete ? data.email : (accountEmail || data.email),
      date_of_birth: data.date_of_birth || null,
      years_training: data.years_training ? parseInt(data.years_training) : null
    };

    console.log('💾 Auto-saving form data...');
    return saveDancerInfo(dbData); // false on failure
  };

  // Only enable auto-save if basic required fields are filled
  const canAutoSave = formData.date_of_birth && formData.first_name && formData.last_name && formData.email;
  // Never auto-save the closed-season view-only form.
  const { lastSaved, isSaving } = useAutoSave(formData, saveFormData, 30000, canAutoSave && !readOnly);

  /**
   * Validate form
   */
  const validate = () => {
    const newErrors = {};

    // Personal Information
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone_number) newErrors.phone_number = 'Phone number is required';
    if (!formData.address_street.trim()) newErrors.address_street = 'Address is required';
    if (!formData.address_city.trim()) newErrors.address_city = 'City is required';
    if (!formData.address_province.trim()) newErrors.address_province = 'Province is required';
    if (!formData.address_postal_code.trim()) newErrors.address_postal_code = 'Postal code is required';

    // Parent/Guardian (if under 18)
    if (isUnder18) {
      if (!formData.parent_guardian_name.trim()) {
        newErrors.parent_guardian_name = 'Parent/Guardian name is required for minors';
      }
      if (!formData.parent_guardian_email.trim()) {
        newErrors.parent_guardian_email = 'Parent/Guardian email is required for minors';
      }
    }

    // Emergency Contact
    if (!formData.emergency_contact_name.trim()) {
      newErrors.emergency_contact_name = 'Emergency contact name is required';
    }
    if (!formData.emergency_contact_relationship.trim()) {
      newErrors.emergency_contact_relationship = 'Relationship is required';
    }
    if (!formData.emergency_contact_phone) {
      newErrors.emergency_contact_phone = 'Emergency contact phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Save and proceed — but only if the save actually landed
    setSubmitError(null);
    const saved = await saveFormData(formData);
    if (saved === false) {
      setSubmitError("We couldn't save your changes. Please check your connection and try again — if it keeps happening, email info@prixducanada.com.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    goToNextStep();
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {submitError && (
        <Alert color="red" mb="md">{submitError}</Alert>
      )}
      {reuseOffer && (
        // In the site's palette and with a filled button: testers skipped the
        // pale yellow alert this used to be.
        <Paper withBorder radius="md" p="md" mb="md" style={{
          textAlign: 'left', background: 'var(--soft-cream)', borderLeft: '4px solid var(--primary-gold)',
        }}>
          <Group gap="xs" mb={6} wrap="nowrap" align="center">
            <IconHistory size={20} color="var(--deep-gold)" style={{ flexShrink: 0 }} />
            <Text fw={700} style={{ color: 'var(--charcoal)' }}>
              Welcome back! We found your {reuseOffer.year} registration.
            </Text>
          </Group>
          <Text size="sm" mb="sm" style={{ textAlign: 'left' }}>
            Save time: fill in your name, birthday, contact, address, parent/guardian and emergency contact
            from last year. Your studio, level and medical information are left for you to fill in fresh.
          </Text>
          <Group gap="sm">
            <Button size="sm" onClick={applyReuse}
              style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}>
              Use my {reuseOffer.year} info
            </Button>
            <Button size="sm" variant="subtle" color="gray" onClick={() => setReuseOffer(null)}>No thanks</Button>
          </Group>
        </Paper>
      )}
      {/* Personal Information Section */}
      <Paper
        radius="md"
        p={{ base: 'sm', sm: 'xl' }}
        withBorder
        style={{
          borderTop: '4px solid var(--primary-gold)',
          marginBottom: '2rem'
        }}
      >
        <Title order={2} size="h3" style={{ marginBottom: '1.5rem', color: 'var(--charcoal)', textAlign: 'left' }}>
          Personal Information
        </Title>

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="First Name"
              placeholder="Enter your first name"
              required
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              error={errors.first_name}
              styles={inputStyles}
              readOnly={isComplete}
              disabled={isComplete}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Last Name"
              placeholder="Enter your last name"
              required
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              error={errors.last_name}
              styles={inputStyles}
              readOnly={isComplete}
              disabled={isComplete}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Name Pronunciation"
              placeholder="For example: Aoife (Ee-fah)"
              value={formData.name_pronunciation}
              onChange={(e) => handleChange('name_pronunciation', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Date of Birth"
              placeholder="MM/DD/YYYY"
              type="date"
              required
              value={formData.date_of_birth}
              onChange={(e) => handleChange('date_of_birth', e.target.value)}
              error={errors.date_of_birth}
              max={new Date().toISOString().split('T')[0]}
              styles={inputStyles}
              readOnly={isComplete}
              disabled={isComplete}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Gender"
              placeholder="Select gender"
              value={formData.gender}
              onChange={(value) => handleChange('gender', value)}
              data={[
                { value: 'Male', label: 'Male' },
                { value: 'Female', label: 'Female' },
                { value: 'Non-binary', label: 'Non-binary' },
                { value: 'Prefer not to say', label: 'Prefer not to say' }
              ]}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Email"
              placeholder="your@email.com"
              type="email"
              required
              value={formData.email}
              error={errors.email}
              description="Your account email. To change it, email info@prixducanada.com."
              styles={inputStyles}
              readOnly
              disabled
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <PhoneInputField
              label="Phone Number"
              required
              value={formData.phone_number}
              onChange={(v) => handleChange('phone_number', v)}
              error={errors.phone_number}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Street Address"
              placeholder="123 Main Street"
              required
              value={formData.address_street}
              onChange={(e) => handleChange('address_street', e.target.value)}
              error={errors.address_street}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="City"
              placeholder="City"
              required
              value={formData.address_city}
              onChange={(e) => handleChange('address_city', e.target.value)}
              error={errors.address_city}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Province/State"
              placeholder="Province"
              required
              value={formData.address_province}
              onChange={(e) => handleChange('address_province', e.target.value)}
              error={errors.address_province}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Postal Code"
              placeholder="A1A 1A1"
              required
              value={formData.address_postal_code}
              onChange={(e) => handleChange('address_postal_code', e.target.value)}
              error={errors.address_postal_code}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Country"
              value={formData.address_country}
              onChange={(e) => handleChange('address_country', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>
        </Grid>

        {/* Parent/Guardian Section (if under 18) */}
        {isUnder18 && (
          <>
            <Divider my="xl" />
            <Title order={3} size="h4" style={{ marginBottom: '1rem', color: 'var(--deep-gold)', textAlign: 'left' }}>
              Parent/Guardian Information
            </Title>
            <Text size="sm" color="dimmed" mb="md" style={{ textAlign: 'left' }}>
              Required for dancers under 18 years of age
            </Text>

            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Parent/Guardian Name"
                  placeholder="Full name"
                  required
                  value={formData.parent_guardian_name}
                  onChange={(e) => handleChange('parent_guardian_name', e.target.value)}
                  error={errors.parent_guardian_name}
                  styles={inputStyles}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Parent/Guardian Email"
                  placeholder="parent@example.com"
                  type="email"
                  required
                  value={formData.parent_guardian_email}
                  onChange={(e) => handleChange('parent_guardian_email', e.target.value)}
                  error={errors.parent_guardian_email}
                  description="Consent form will be sent to this email"
                  styles={inputStyles}
                />
              </Grid.Col>
            </Grid>
          </>
        )}
      </Paper>

      {/* Dance Background Section */}
      <Paper
        radius="md"
        p={{ base: 'sm', sm: 'xl' }}
        withBorder
        style={{
          borderTop: '4px solid var(--primary-gold)',
          marginBottom: '2rem'
        }}
      >
        <Title order={2} size="h3" style={{ marginBottom: '1.5rem', color: 'var(--charcoal)', textAlign: 'left' }}>
          Dance Background
        </Title>

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Years of Training"
              placeholder="Number of years"
              type="number"
              value={formData.years_training}
              onChange={(e) => handleChange('years_training', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Current Studio/School"
              placeholder="Your dance studio or school"
              value={formData.current_studio}
              onChange={(e) => handleChange('current_studio', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Teacher's Name (optional)"
              placeholder="Your ballet teacher"
              value={formData.teacher_name}
              onChange={(e) => handleChange('teacher_name', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Previous Competition Experience"
              placeholder="Select experience level"
              value={formData.competition_experience}
              onChange={(value) => handleChange('competition_experience', value)}
              data={[
                { value: 'None', label: 'None' },
                { value: '1-2 competitions', label: '1-2 competitions' },
                { value: '3-5 competitions', label: '3-5 competitions' },
                { value: '5+ competitions', label: '5+ competitions' }
              ]}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Current Level/Grade"
              placeholder="e.g., Advanced 1, RAD Intermediate"
              value={formData.current_level}
              onChange={(e) => handleChange('current_level', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Emergency Contact Section */}
      <Paper
        radius="md"
        p={{ base: 'sm', sm: 'xl' }}
        withBorder
        style={{
          borderTop: '4px solid var(--primary-gold)',
          marginBottom: '2rem'
        }}
      >
        <Title order={2} size="h3" style={{ marginBottom: '1.5rem', color: 'var(--charcoal)', textAlign: 'left' }}>
          Emergency Contact & Medical Information
        </Title>

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Emergency Contact Name"
              placeholder="Full name"
              required
              value={formData.emergency_contact_name}
              onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
              error={errors.emergency_contact_name}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Relationship"
              placeholder="e.g., Parent, Spouse, Sibling"
              required
              value={formData.emergency_contact_relationship}
              onChange={(e) => handleChange('emergency_contact_relationship', e.target.value)}
              error={errors.emergency_contact_relationship}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <PhoneInputField
              label="Emergency Phone Number"
              required
              value={formData.emergency_contact_phone}
              onChange={(v) => handleChange('emergency_contact_phone', v)}
              error={errors.emergency_contact_phone}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Emergency Email"
              placeholder="email@example.com"
              type="email"
              value={formData.emergency_contact_email}
              onChange={(e) => handleChange('emergency_contact_email', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Medical Conditions/Notes"
              placeholder="Please list any relevant medical conditions, allergies, or medications"
              minRows={3}
              value={formData.medical_conditions}
              onChange={(e) => handleChange('medical_conditions', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Special Accommodations Needed"
              placeholder="Any special accommodations or accessibility requirements"
              minRows={2}
              value={formData.special_accommodations}
              onChange={(e) => handleChange('special_accommodations', e.target.value)}
              styles={inputStyles}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Auto-save Indicator */}
      {lastSaved && (
        <Text size="sm" color="dimmed" mb="md" style={{ textAlign: 'left' }}>
          Last saved: {lastSaved.toLocaleTimeString()}
          {isSaving && ' (Saving...)'}
        </Text>
      )}

      </fieldset>

      {/* Navigation Buttons */}
      <Group justify="space-between" mt="xl">
        <Button variant="subtle" onClick={goToPreviousStep} disabled={saving}>
          Back
        </Button>

        {readOnly ? (
          <PrimaryButton type="button" onClick={() => goToStep && goToStep(2)}>
            Next: Consent →
          </PrimaryButton>
        ) : (
          <PrimaryButton type="submit" loading={saving}>
            Save & Continue
          </PrimaryButton>
        )}
      </Group>
    </form>
  );
};

export default Step1Information;
